import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { LogOut, RefreshCw, Phone, Mail, Download } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { LogoMark } from "../components/Logo";

const STATUSES = ["pending", "contacted", "scheduled", "closed"];
const statusCls = {
  pending: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  contacted: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  scheduled: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  closed: "bg-slate-500/15 text-slate-400 border-slate-500/30",
};
const inputCls = "rounded-lg border border-gold/20 bg-navy-950/80 px-4 py-2.5 text-sm text-slate-100 placeholder-slate-500 outline-none focus:border-gold focus:ring-2 focus:ring-gold/30";

function Login({ onLogin }) {
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", form);
      onLogin(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-navy-950 px-5">
      <motion.form
        data-testid="admin-login-form"
        onSubmit={submit}
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-sm rounded-2xl border border-gold/25 bg-navy-900 p-8"
      >
        <div className="mb-8 flex flex-col items-center gap-3 text-center">
          <LogoMark size={52} />
          <h1 className="font-display text-xl font-bold text-slate-50">Staff Access</h1>
          <p className="text-xs text-slate-400">Heidari Law Group — Enquiries Console</p>
        </div>
        <div className="space-y-4">
          <input data-testid="admin-login-email-input" type="email" required placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={`${inputCls} w-full`} />
          <input data-testid="admin-login-password-input" type="password" required placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={`${inputCls} w-full`} />
          {error && <p data-testid="admin-login-error" className="rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-2.5 text-sm text-red-300">{error}</p>}
          <button data-testid="admin-login-submit-btn" disabled={loading} className="w-full rounded-full bg-gradient-to-r from-[#D4AF37] to-[#B38F32] py-3 text-sm font-bold text-navy-950 transition-all hover:from-[#E6CA65] active:scale-[0.98] disabled:opacity-60">
            {loading ? "Signing in..." : "Sign In"}
          </button>
          <a href="/" className="block text-center text-xs text-slate-500 hover:text-gold">← Back to website</a>
        </div>
      </motion.form>
    </div>
  );
}

export default function Admin() {
  const [user, setUser] = useState(undefined);
  const [enquiries, setEnquiries] = useState([]);
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("all");
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    const params = {};
    if (tab !== "all") params.type = tab;
    if (statusFilter) params.status = statusFilter;
    if (search) params.search = search;
    const [list, st] = await Promise.all([
      api.get("/enquiries", { params }),
      api.get("/enquiries/stats"),
    ]);
    setEnquiries(list.data);
    setStats(st.data);
  }, [tab, statusFilter, search]);

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(null));
  }, []);

  useEffect(() => {
    if (user) load().catch(() => {});
  }, [user, load]);

  if (user === undefined) return <div className="flex min-h-screen items-center justify-center bg-navy-950 text-gold font-mono text-sm">Loading…</div>;
  if (user === null) return <Login onLogin={setUser} />;

  const updateStatus = async (id, status) => {
    await api.patch(`/enquiries/${id}`, { status });
    load();
  };

  const exportCsv = () => {
    const rows = [["Type", "Name", "Phone", "Email", "Case Type", "Preferred Date", "Preferred Time", "Message", "Status", "Created"]];
    enquiries.forEach((e) => rows.push([e.type, e.name, e.phone, e.email, e.case_type, e.preferred_date, e.preferred_time, (e.message || "").replace(/\n/g, " "), e.status, e.created_at]));
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = "heidari-enquiries.csv";
    a.click();
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(null);
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-navy-950 px-5 py-8 lg:px-10">
      <div className="mx-auto max-w-7xl">
        <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <LogoMark size={40} />
            <div>
              <h1 className="font-display text-xl font-bold text-slate-50">Enquiries Console</h1>
              <p className="text-xs text-slate-400">Signed in as {user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="admin-refresh-btn" onClick={load} className="flex items-center gap-2 rounded-full border border-gold/30 px-4 py-2 text-xs font-semibold text-gold hover:bg-gold/10"><RefreshCw size={14} /> Refresh</button>
            <button data-testid="admin-export-btn" onClick={exportCsv} className="flex items-center gap-2 rounded-full border border-gold/30 px-4 py-2 text-xs font-semibold text-gold hover:bg-gold/10"><Download size={14} /> Export CSV</button>
            <button data-testid="admin-logout-btn" onClick={logout} className="flex items-center gap-2 rounded-full border border-red-500/30 px-4 py-2 text-xs font-semibold text-red-300 hover:bg-red-500/10"><LogOut size={14} /> Logout</button>
          </div>
        </div>

        {stats && (
          <div className="mb-8 grid grid-cols-2 gap-4 lg:grid-cols-5">
            {[["Today", stats.today, "admin-stat-today"], ["Total", stats.total, "admin-stat-total"], ["Pending", stats.pending, "admin-stat-pending"], ["Pending Callbacks", stats.pending_callbacks, "admin-stat-callbacks"], ["Scheduled", stats.scheduled, "admin-stat-scheduled"]].map(([label, val, id]) => (
              <div key={id} data-testid={id} className="rounded-xl border border-gold/15 bg-navy-900 p-5">
                <div className="font-display text-2xl font-bold text-gold">{val}</div>
                <div className="mt-1 text-xs text-slate-400">{label}</div>
              </div>
            ))}
          </div>
        )}

        <div className="mb-6 flex flex-wrap items-center gap-3">
          <div className="flex rounded-full border border-gold/20 bg-navy-900 p-1">
            {[["all", "All"], ["consultation", "Consultations"], ["callback", "Callbacks"]].map(([key, label]) => (
              <button key={key} data-testid={`admin-tab-${key}`} onClick={() => setTab(key)} className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${tab === key ? "bg-gold text-navy-950" : "text-slate-300 hover:text-gold"}`}>
                {label}
              </button>
            ))}
          </div>
          <select data-testid="admin-status-filter-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={inputCls}>
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <input data-testid="admin-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, phone, email…" className={`${inputCls} flex-1 min-w-[200px]`} />
        </div>

        <div className="overflow-x-auto rounded-2xl border border-gold/15">
          <table data-testid="admin-enquiries-table" className="w-full min-w-[900px] text-left text-sm">
            <thead className="border-b border-gold/15 bg-navy-900 text-xs uppercase tracking-wider text-gold/70">
              <tr>
                <th className="px-5 py-3.5">Received</th>
                <th className="px-5 py-3.5">Type</th>
                <th className="px-5 py-3.5">Contact</th>
                <th className="px-5 py-3.5">Case</th>
                <th className="px-5 py-3.5">Preference</th>
                <th className="px-5 py-3.5">Message</th>
                <th className="px-5 py-3.5">Status</th>
              </tr>
            </thead>
            <tbody>
              {enquiries.length === 0 && (
                <tr><td colSpan={7} data-testid="admin-empty-state" className="px-5 py-12 text-center text-slate-500">No enquiries found.</td></tr>
              )}
              {enquiries.map((e) => (
                <tr key={e.id} data-testid={`admin-enquiry-row-${e.id}`} className="border-b border-gold/10 transition-colors hover:bg-navy-900/60">
                  <td className="px-5 py-4 font-mono text-xs text-slate-400 whitespace-nowrap">{new Date(e.created_at).toLocaleString()}</td>
                  <td className="px-5 py-4">
                    <span className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ${e.type === "callback" ? "border-sky-500/30 bg-sky-500/10 text-sky-300" : "border-gold/30 bg-gold/10 text-gold"}`}>{e.type}</span>
                  </td>
                  <td className="px-5 py-4">
                    <div className="font-medium text-slate-100">{e.name}</div>
                    <div className="mt-1 flex items-center gap-1.5 text-xs text-slate-400"><Phone size={11} /> <a className="hover:text-gold" href={`tel:${e.phone}`}>{e.phone}</a></div>
                    {e.email && <div className="mt-0.5 flex items-center gap-1.5 text-xs text-slate-400"><Mail size={11} /> <a className="hover:text-gold" href={`mailto:${e.email}`}>{e.email}</a></div>}
                  </td>
                  <td className="px-5 py-4 text-xs text-slate-300">{e.case_type || "—"}</td>
                  <td className="px-5 py-4 text-xs text-slate-300 whitespace-nowrap">{[e.preferred_date, e.preferred_time].filter(Boolean).join(" · ") || "—"}</td>
                  <td className="max-w-[220px] px-5 py-4 text-xs text-slate-400"><span className="line-clamp-3">{e.message || "—"}</span></td>
                  <td className="px-5 py-4">
                    <select
                      data-testid={`admin-status-select-${e.id}`}
                      value={e.status}
                      onChange={(ev) => updateStatus(e.id, ev.target.value)}
                      className={`rounded-full border px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide outline-none ${statusCls[e.status] || statusCls.pending} bg-navy-950`}
                    >
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
